// components/TwoFactorSetupModal.jsx
import React, { useEffect, useState } from "react";
import {
  tfa2faStatus,
  tfa2faSetupBegin,
  tfa2faSetupConfirm,
  tfa2faDisable,
} from "../services/api";

export default function TwoFactorSetupModal({ open, onClose }) {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [step, setStep] = useState("status"); // status | setup | confirm | recovery | disable
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep("status");
    setError(null);
    setCode("");
    setLoading(true);
    tfa2faStatus()
      .then((d) => setEnabled(!!d.enabled))
      .catch(() => setEnabled(false))
      .finally(() => setLoading(false));
  }, [open]);

  const handleBeginSetup = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await tfa2faSetupBegin();
      setQrCode(data.qr_code);
      setSecret(data.secret);
      setStep("setup");
    } catch (e) {
      setError(e?.message || "Failed to start setup");
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const data = await tfa2faSetupConfirm(code.trim());
      setRecoveryCodes(data.recovery_codes || []);
      setEnabled(true);
      setStep("recovery");
    } catch (e) {
      setError(e?.message || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await tfa2faDisable(code.trim());
      setEnabled(false);
      setStep("status");
      setCode("");
    } catch (e) {
      setError(e?.message || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const inputStyle = {
    border: "1px solid #d1d5db", borderRadius: 8, padding: "10px 14px",
    fontSize: 18, width: "100%", boxSizing: "border-box",
    textAlign: "center", letterSpacing: 3, fontFamily: "monospace",
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true">
      <div className="modal-card" style={{ width: 480, maxWidth: "100%" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>Two-Factor Authentication</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {loading ? (
          <div style={{ color: "#6b7280", fontSize: 13 }}>Loading...</div>
        ) : step === "status" ? (
          <div>
            <div style={{
              padding: 16, borderRadius: 10, marginBottom: 16,
              background: enabled ? "#f0fdf4" : "#f9fafb",
              border: enabled ? "1px solid #bbf7d0" : "1px solid #e5e7eb",
            }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: enabled ? "#166534" : "#374151", marginBottom: 6 }}>
                {enabled ? "2FA is enabled" : "2FA is not enabled"}
              </div>
              <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.5 }}>
                {enabled
                  ? "Your account is protected with two-factor authentication. You need your authenticator app to log in."
                  : "Add an extra layer of security to your account. You'll need an authenticator app like Microsoft Authenticator, Google Authenticator, or Authy."
                }
              </div>
            </div>

            {enabled ? (
              <button className="btn-secondary" style={{ color: "#ef4444", borderColor: "#fca5a5" }}
                onClick={() => { setStep("disable"); setCode(""); setError(null); }}>
                Disable 2FA
              </button>
            ) : (
              <button className="btn-primary" onClick={handleBeginSetup} disabled={busy}>
                {busy ? "Setting up..." : "Enable 2FA"}
              </button>
            )}
          </div>

        ) : step === "setup" ? (
          <div>
            <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.6, marginBottom: 16 }}>
              <strong>Step 1:</strong> Scan this QR code with your authenticator app.
            </div>

            {qrCode && (
              <div style={{ textAlign: "center", marginBottom: 16 }}>
                <img src={qrCode} alt="2FA QR Code" style={{ width: 200, height: 200, borderRadius: 8 }} />
              </div>
            )}

            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 16, textAlign: "center" }}>
              Can't scan? Enter this code manually: <br />
              <code style={{ fontSize: 14, fontWeight: 700, color: "#111827", letterSpacing: 2 }}>{secret}</code>
            </div>

            <form onSubmit={handleConfirm}>
              <div style={{ fontSize: 13, color: "#374151", marginBottom: 8 }}>
                <strong>Step 2:</strong> Enter the 6-digit code from your app to confirm:
              </div>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
                style={inputStyle}
                autoFocus
                autoComplete="one-time-code"
                disabled={busy}
              />
              {error && <div style={{ marginTop: 8, fontSize: 13, color: "#ef4444" }}>{error}</div>}
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16 }}>
                <button type="button" className="btn-secondary" onClick={() => setStep("status")} disabled={busy}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={busy || !code.trim()}>
                  {busy ? "Verifying..." : "Confirm"}
                </button>
              </div>
            </form>
          </div>

        ) : step === "recovery" ? (
          <div>
            <div style={{
              padding: 16, borderRadius: 10, marginBottom: 16,
              background: "#fffbeb", border: "1px solid #fde68a",
            }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: "#92400e", marginBottom: 8 }}>
                Save your recovery codes
              </div>
              <div style={{ fontSize: 13, color: "#78350f", lineHeight: 1.6 }}>
                If you lose access to your authenticator app, you can use these codes to log in.
                Each code can only be used once. Store them in a safe place.
              </div>
            </div>

            <div style={{
              background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8,
              padding: 16, fontFamily: "monospace", fontSize: 16, lineHeight: 2,
              textAlign: "center", marginBottom: 16,
            }}>
              {recoveryCodes.map((c, i) => (
                <div key={i}>{c}</div>
              ))}
            </div>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                className="btn-secondary"
                onClick={() => {
                  const text = recoveryCodes.join("\n");
                  navigator.clipboard.writeText(text).catch(() => {});
                }}
              >
                Copy to clipboard
              </button>
              <button className="btn-primary" onClick={onClose}>
                Done
              </button>
            </div>
          </div>

        ) : step === "disable" ? (
          <form onSubmit={handleDisable}>
            <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.6, marginBottom: 16 }}>
              To disable 2FA, enter a code from your authenticator app to confirm it's you.
            </div>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              style={inputStyle}
              autoFocus
              autoComplete="one-time-code"
              disabled={busy}
            />
            {error && <div style={{ marginTop: 8, fontSize: 13, color: "#ef4444" }}>{error}</div>}
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16 }}>
              <button type="button" className="btn-secondary" onClick={() => { setStep("status"); setCode(""); setError(null); }} disabled={busy}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" style={{ background: "#ef4444" }} disabled={busy || !code.trim()}>
                {busy ? "Disabling..." : "Disable 2FA"}
              </button>
            </div>
          </form>
        ) : null}
      </div>
    </div>
  );
}
